// MARCHING-SQUARES cell tessellation for the terra heightfield. A feature's carve is baked SHARPLY into
// the per-node height grid; on its own that would staircase on the 50-stud grid, so for any cell the
// feature contour crosses we re-triangulate the cell with an edge lying ON the contour — the sharp height
// transition then follows the true diagonal/curved iso-line instead of the grid.
//
// The SAME per-cell triangulation is consumed by BOTH the mesh build (emit world triangles + interpolate
// color/glow/pad) AND field.heightAt (point-locate a query + barycentric-interpolate height), so the
// load-bearing invariant "mesh height ≡ unit feet ≡ nav height" holds even at the sharpened edges — units,
// the player, projectiles and LOS all sit exactly on the rendered surface.
//
// Each tessellated vertex stores its interpolation over the cell's 4 CORNERS (weights sum to 1), so any
// per-node quantity (height, vertex color, glow, pad) interpolates identically in both consumers.

export interface CVert {
  u: number; // local cell coords in [0,1] (u = +X across the cell, v = +Z)
  v: number;
  refs: { c: number; w: number }[]; // corner refs (c in 0..3) with weights summing to 1
}
export type CTri = [CVert, CVert, CVert];

// Corner local coords, matching the mesh's node layout for a cell (gx,gz):
//   0 = (gx,   gz)   → (0,0)      1 = (gx+1, gz)   → (1,0)
//   2 = (gx,   gz+1) → (0,1)      3 = (gx+1, gz+1) → (1,1)
const CU = [0, 1, 0, 1];
const CV = [0, 0, 1, 1];
// Boundary loop of corners in the SAME winding sense as STD / the mesh's a-d-b split (0 → 2 → 3 → 1),
// so fanned contour triangles face +Y like the standard cells (computeVertexNormals keys off winding).
const LOOP = [0, 2, 3, 1];

const cornerVert = (c: number): CVert => ({ u: CU[c], v: CV[c], refs: [{ c, w: 1 }] });
const edgeVert = (a: number, b: number, t: number): CVert => ({
  u: CU[a] + (CU[b] - CU[a]) * t,
  v: CV[a] + (CV[b] - CV[a]) * t,
  refs: [{ c: a, w: 1 - t }, { c: b, w: t }],
});

// The standard (non-contour) split, IDENTICAL to the mesh's a-d-b / b-d-e triangles and to heightAt's
// u+v<=1 diagonal — so non-crossing cells are byte-for-byte unchanged.
const STD: CTri[] = [
  [cornerVert(0), cornerVert(2), cornerVert(1)],
  [cornerVert(1), cornerVert(2), cornerVert(3)],
];

/** Fan-triangulate a convex polygon of CVerts. */
function fan(poly: CVert[], out: CTri[]): void {
  for (let i = 1; i + 1 < poly.length; i++) out.push([poly[0], poly[i], poly[i + 1]]);
}

/**
 * Tessellate a cell from its 4 corner feature values against `iso`.
 *  - all-in or all-out → the standard 2-triangle split (no contour here).
 *  - a single contour segment (14 of 16 cases) → split into inside + outside convex polygons, each fanned,
 *    the shared contour vertices carrying the interpolated (sharp) height so the transition follows the curve.
 *  - the 2 ambiguous SADDLE cases (opposite corners in) → emit all four edge crossings and fan them from the
 *    cell center (8 triangles), staying watertight against tessellated neighbors (STD would T-junction).
 * Returns triangles whose vertices interpolate the cell's 4 corners; caller supplies the corner values.
 */
export function tessCell(f0: number, f1: number, f2: number, f3: number, iso: number): CTri[] {
  const f = [f0, f1, f2, f3];
  const inside = [f0 > iso, f1 > iso, f2 > iso, f3 > iso];
  let nIn = 0;
  for (let i = 0; i < 4; i++) if (inside[i]) nIn++;
  if (nIn === 0 || nIn === 4) return STD; // no crossing

  // SADDLE (opposite corners in: cases 5/10) — every edge crosses. Fan from the cell center so all four
  // edge-crossing vertices are present (falling back to STD would T-junction against tessellated neighbors).
  if (nIn === 2 && inside[0] === inside[3] && inside[1] === inside[2]) {
    const center: CVert = { u: 0.5, v: 0.5, refs: [{ c: 0, w: 0.25 }, { c: 1, w: 0.25 }, { c: 2, w: 0.25 }, { c: 3, w: 0.25 }] };
    const boundary: CVert[] = [];
    for (let e = 0; e < 4; e++) {
      const a = LOOP[e], b = LOOP[(e + 1) % 4];
      boundary.push(cornerVert(a));
      boundary.push(edgeVert(a, b, (iso - f[a]) / (f[b] - f[a])));
    }
    const out: CTri[] = [];
    for (let i = 0; i < boundary.length; i++) out.push([center, boundary[i], boundary[(i + 1) % boundary.length]]);
    return out;
  }

  const insidePoly: CVert[] = [];
  const outsidePoly: CVert[] = [];
  for (let e = 0; e < 4; e++) {
    const a = LOOP[e], b = LOOP[(e + 1) % 4];
    (inside[a] ? insidePoly : outsidePoly).push(cornerVert(a));
    if (inside[a] !== inside[b]) {
      const t = (iso - f[a]) / (f[b] - f[a]); // guaranteed finite: f[a] != f[b] since memberships differ
      const xv = edgeVert(a, b, t);
      insidePoly.push(xv);
      outsidePoly.push(xv);
    }
  }
  const out: CTri[] = [];
  if (insidePoly.length >= 3) fan(insidePoly, out);
  if (outsidePoly.length >= 3) fan(outsidePoly, out);
  return out;
}

/** Barycentric weight of point p in triangle (a,b,c) using local (u,v). Returns null if outside. */
function bary(pu: number, pv: number, t: CTri): [number, number, number] | null {
  const [A, B, C] = t;
  const d = (B.v - C.v) * (A.u - C.u) + (C.u - B.u) * (A.v - C.v);
  if (Math.abs(d) < 1e-12) return null; // degenerate
  const wa = ((B.v - C.v) * (pu - C.u) + (C.u - B.u) * (pv - C.v)) / d;
  const wb = ((C.v - A.v) * (pu - C.u) + (A.u - C.u) * (pv - C.v)) / d;
  const wc = 1 - wa - wb;
  const eps = -1e-6;
  if (wa < eps || wb < eps || wc < eps) return null;
  return [wa, wb, wc];
}

/** Interpolate a per-corner quantity at a CVert (via its corner refs). */
export function vertVal(vert: CVert, corners: ArrayLike<number>): number {
  let s = 0;
  for (const r of vert.refs) s += corners[r.c] * r.w;
  return s;
}

/** Sample the tessellated height at local (u,v) within a cell whose 4 corner heights are given. Point-locates
 *  the containing triangle and barycentric-interpolates — the EXACT surface the mesh renders. */
export function sampleTess(tris: CTri[], u: number, v: number, cornerH: ArrayLike<number>): number | null {
  for (const t of tris) {
    const w = bary(u, v, t);
    if (w) return w[0] * vertVal(t[0], cornerH) + w[1] * vertVal(t[1], cornerH) + w[2] * vertVal(t[2], cornerH);
  }
  return null;
}
